import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Select, Button, TimePicker, Input, Spin, Tooltip, message } from 'antd';
import { CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'core/types';
import { FtcAnswer, FtcSessionEntry } from 'core/sites/types';
import {
    fetchUpdateEntries,
    updateEntry,
    sendUpdate,
    UpdateEntryPayload,
} from 'core/ftc/ftcApi';
import DoorCctvCodesModal from 'pages/SiteInfo/components/DoorCctvCodesModal';
import dayjs from 'dayjs';
import './ftcUpdateModal.scss';
import { MAX_LONG_TEXT_LENGTH } from 'core/constants';
import {
    AnswerField,
    EMPTY_FORM,
    FormState,
    REQUIRED_FIELDS,
    applyDefaults,
    applyFieldRules,
    isFieldMissing,
} from './formRules';

// Matches the backend khcReference validation (max:100).
const MAX_KHC_REFERENCE_LENGTH = 100;

const { TextArea } = Input;

interface FtcUpdateModalProps {
    visible: boolean;
    onClose: () => void;
}

interface AnswerSelectOption {
    value: FtcAnswer;
    label: string;
}

const ANSWER_OPTIONS: AnswerSelectOption[] = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'na', label: 'N/A' },
];

const entryToForm = (entry: FtcSessionEntry): FormState => applyDefaults({
    timeOn: entry.timeOn ? dayjs(entry.timeOn, 'HH:mm') : null,
    timeOff: entry.timeOff ? dayjs(entry.timeOff, 'HH:mm') : null,
    internalPatrol: entry.internalPatrol,
    externalPatrol: entry.externalPatrol,
    idsFobHeld: entry.idsFobHeld,
    idsFobWorks: entry.idsFobWorks,
    storeKeysHeld: entry.storeKeysHeld,
    storeKeysWork: entry.storeKeysWork,
    shutterKeyHeld: entry.shutterKeyHeld,
    shutterKeyWorks: entry.shutterKeyWorks,
    responseSetIdsOnDeparture: entry.responseSetIdsOnDeparture,
    remoteEngSetOnDeparture: entry.remoteEngSetOnDeparture,
    khcReference: entry.khcReference || '',
    comments: entry.comments || '',
});

const formToPayload = (entryId: number, form: FormState): UpdateEntryPayload => ({
    entryId,
    timeOn: form.timeOn ? form.timeOn.format('HH:mm') : null,
    timeOff: form.timeOff ? form.timeOff.format('HH:mm') : null,
    internalPatrol: form.internalPatrol,
    externalPatrol: form.externalPatrol,
    idsFobHeld: form.idsFobHeld,
    idsFobWorks: form.idsFobWorks,
    storeKeysHeld: form.storeKeysHeld,
    storeKeysWork: form.storeKeysWork,
    shutterKeyHeld: form.shutterKeyHeld,
    shutterKeyWorks: form.shutterKeyWorks,
    responseSetIdsOnDeparture: form.responseSetIdsOnDeparture,
    remoteEngSetOnDeparture: form.remoteEngSetOnDeparture,
    khcReference: form.khcReference.trim() || null,
    comments: form.comments.trim() || null,
});

const FtcUpdateModal: FC<FtcUpdateModalProps> = ({ visible, onClose }) => {
    const dispatch = useDispatch<AppDispatch>();
    const [entries, setEntries] = useState<FtcSessionEntry[]>([]);
    const [selectedEntryId, setSelectedEntryId] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [doorCodesVisible, setDoorCodesVisible] = useState(false);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);

    // Latest form/entry state, so saving on close or on a store switch never
    // persists a stale snapshot from an old render.
    const latestRef = useRef({ entries, selectedEntryId, form });
    latestRef.current = { entries, selectedEntryId, form };

    const setField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const loadEntries = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await dispatch(fetchUpdateEntries()).unwrap();
            setEntries(result.entries || []);
            if (result.entries?.length > 0) {
                setSelectedEntryId(result.entries[0].id);
            }
        } catch {
            // Error handled by thunk
        } finally {
            setIsLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        if (visible) {
            loadEntries();
        } else {
            setEntries([]);
            setSelectedEntryId(null);
            setDoorCodesVisible(false);
            setForm(EMPTY_FORM);
        }
    }, [visible, loadEntries]);

    const selectedEntry = entries.find((e) => e.id === selectedEntryId) || null;

    useEffect(() => {
        if (selectedEntry) {
            setForm(entryToForm(selectedEntry));
        }
        // Only reload the form when a different store is picked; re-running on
        // every entry object change would discard in-progress typing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEntryId]);

    // Persist whatever has been entered so far for the given store. Returns the
    // saved entry, or null when there was nothing to save.
    const persistEntry = useCallback(async (entryId: number | null): Promise<FtcSessionEntry | null> => {
        const { entries: currentEntries, form: currentForm } = latestRef.current;
        const entry = currentEntries.find((e) => e.id === entryId);
        if (!entry) return null;

        const payload = formToPayload(entry.id, currentForm);
        const stored = formToPayload(entry.id, entryToForm(entry));
        if (JSON.stringify(payload) === JSON.stringify(stored)) return null;

        setIsSaving(true);
        try {
            const updated = await dispatch(updateEntry(payload)).unwrap();
            setEntries((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
            return updated;
        } catch {
            // Error handled by thunk
            return null;
        } finally {
            setIsSaving(false);
        }
    }, [dispatch]);

    const handleSelectStore = async (entryId: number) => {
        await persistEntry(selectedEntryId);
        setSelectedEntryId(entryId);
    };

    const handleClose = async () => {
        await persistEntry(latestRef.current.selectedEntryId);
        onClose();
    };

    // The send covers what is stored, so the store on screen is saved first.
    const handleSendReport = async () => {
        setIsSending(true);
        try {
            await persistEntry(latestRef.current.selectedEntryId);
            const result = await dispatch(sendUpdate()).unwrap();
            message.success(result.message || 'FTC report sent successfully');
            onClose();
        } catch {
            // Error handled by thunk; the modal stays open so the send can be retried.
        } finally {
            setIsSending(false);
        }
    };

    // Fields on the store being edited that still have no answer.
    const isMissing = (field: keyof FormState) => isFieldMissing(form, field);

    const outstandingCount = REQUIRED_FIELDS.filter(isMissing).length;

    // Completion of the store on screen follows what has been typed rather than
    // what is saved, so the tick and the Send button react straight away.
    const isEntryComplete = (entry: FtcSessionEntry) => (
        entry.id === selectedEntryId ? outstandingCount === 0 : entry.isComplete
    );

    // A store already covered by an earlier update is not sent a second time, so
    // only the outstanding ones gate the send. Every one of them has to be
    // completed before the update can go out.
    const pendingEntries = entries.filter((entry) => !entry.isUpdateSent);
    const readyEntries = pendingEntries.filter(isEntryComplete);
    const incompleteEntries = pendingEntries.filter((entry) => !isEntryComplete(entry));
    const alreadySentCount = entries.length - pendingEntries.length;
    const canSendReport = readyEntries.length > 0 && incompleteEntries.length === 0;

    // Why the send is unavailable, shown on hover over the disabled button.
    const sendBlockedReason = !canSendReport && incompleteEntries.length === 0 && alreadySentCount > 0
        ? 'Every store has already been sent.'
        : '';

    // Marks what is still needed before the update report can be sent.
    const renderRequired = (label: string, field: keyof FormState) => (
        isMissing(field) ? <span className="error-message">{label} is required</span> : null
    );

    // Answering a field can answer others (see formRules), so the whole form is
    // recalculated rather than the one field being set.
    const answerField = (field: AnswerField, value: FtcAnswer) => {
        setForm((prev) => applyFieldRules(prev, field, value));
    };

    const renderAnswerSelect = (
        label: string,
        field: AnswerField,
        value: FtcAnswer | null,
    ) => (
        <div className="field-group">
            <label className="field-label">{label}</label>
            <Select<FtcAnswer | null, AnswerSelectOption>
                value={value}
                onChange={(val) => answerField(field, val as FtcAnswer)}
                options={ANSWER_OPTIONS}
                placeholder="Select"
                className="bool-select" />
            {renderRequired(label, field)}
        </div>
    );

    return (
        <Modal
            title="Update Fail to Close"
            open={visible}
            onCancel={handleClose}
            footer={null}
            centered
            width={950}
            wrapClassName="ftc-update-modal">
            {isLoading && (
                <div className="modal-loading">
                    <Spin size="large" />
                </div>
            )}
            {!isLoading && entries.length === 0 && (
                <div className="empty-state">No FTC stores to update. Send a Fail to Close report first.</div>
            )}
            {!isLoading && entries.length > 0 && (
                <>
                    <div className="store-selector">
                        <label className="field-label">Select Store</label>
                        <Select
                            value={selectedEntryId}
                            onChange={handleSelectStore}
                            className="store-select"
                            options={entries.map((e) => ({
                                value: e.id,
                                label: (
                                    <span>
                                        {e.storeCode || '-'} - {e.siteName || 'Unknown'} ({e.sitePostcode || '-'})
                                        {isEntryComplete(e) && <CheckCircleOutlined style={{ color: '#da0812', marginLeft: 8 }} />}
                                    </span>
                                ),
                            }))} />
                    </div>

                    {selectedEntry && (
                        <>
                            <div className="store-info">
                                <span><strong>Store Code:</strong> {selectedEntry.storeCode || '-'}</span>
                                <span><strong>Store Name:</strong> {selectedEntry.siteName || '-'}</span>
                                <span><strong>Post Code:</strong> {selectedEntry.sitePostcode || '-'}</span>
                                {selectedEntry.siteEncryptedId && (
                                    <button
                                        type="button"
                                        className="door-codes-button"
                                        onClick={() => setDoorCodesVisible(true)}>
                                        View Door Codes
                                    </button>
                                )}
                            </div>

                            <div className="form-row">
                                <div className="field-group">
                                    <label className="field-label">Time On</label>
                                    <TimePicker
                                        value={form.timeOn}
                                        onChange={(val) => setField('timeOn', val)}
                                        format="HH:mm"
                                        className="time-picker" />
                                    {renderRequired('Time On', 'timeOn')}
                                </div>
                                <div className="field-group">
                                    <label className="field-label">Time Off</label>
                                    <TimePicker
                                        value={form.timeOff}
                                        onChange={(val) => setField('timeOff', val)}
                                        format="HH:mm"
                                        className="time-picker" />
                                    {renderRequired('Time Off', 'timeOff')}
                                </div>
                            </div>

                            <div className="form-row">
                                {renderAnswerSelect('Internal Patrol', 'internalPatrol', form.internalPatrol)}
                                {renderAnswerSelect('External Patrol', 'externalPatrol', form.externalPatrol)}
                                {renderAnswerSelect('IDS Fob held', 'idsFobHeld', form.idsFobHeld)}
                                {renderAnswerSelect('IDS Fob Works', 'idsFobWorks', form.idsFobWorks)}
                            </div>

                            <div className="form-row">
                                {renderAnswerSelect('Store Keys held', 'storeKeysHeld', form.storeKeysHeld)}
                                {renderAnswerSelect('Store Keys work', 'storeKeysWork', form.storeKeysWork)}
                                {renderAnswerSelect('Shutter Key / Remote held', 'shutterKeyHeld', form.shutterKeyHeld)}
                                {renderAnswerSelect('Shutter Key / Remote works', 'shutterKeyWorks', form.shutterKeyWorks)}
                            </div>

                            <div className="form-row">
                                {renderAnswerSelect('Response set IDS on Departure', 'responseSetIdsOnDeparture', form.responseSetIdsOnDeparture)}
                                {renderAnswerSelect('Remote Eng Set on Departure', 'remoteEngSetOnDeparture', form.remoteEngSetOnDeparture)}
                            </div>

                            <div className="form-row">
                                <div className="field-group">
                                    <label className="field-label">KHC Reference</label>
                                    <Input
                                        value={form.khcReference}
                                        onChange={(e) => setField('khcReference', e.target.value)}
                                        placeholder="Enter KHC reference"
                                        maxLength={MAX_KHC_REFERENCE_LENGTH}
                                        className="text-input" />
                                    {renderRequired('KHC Reference', 'khcReference')}
                                </div>
                            </div>

                            <div className="comments-section">
                                <label className="field-label">Comments</label>
                                <TextArea
                                    value={form.comments}
                                    onChange={(e) => setField('comments', e.target.value)}
                                    placeholder="Enter comments..."
                                    maxLength={MAX_LONG_TEXT_LENGTH}
                                    className="comments-textarea" />
                            </div>

                        </>
                    )}

                    <div className="modal-buttons">
                        {isSaving && (
                            <span className="saving-indicator">
                                <LoadingOutlined /> Saving
                            </span>
                        )}
                        <Tooltip title={sendBlockedReason}>
                            <span>
                                <Button
                                    type="primary"
                                    className="send-report-button"
                                    loading={isSending}
                                    disabled={!canSendReport}
                                    onClick={handleSendReport}>
                                    {alreadySentCount > 0 ? 'Send New Stores' : 'Send Report'}
                                </Button>
                            </span>
                        </Tooltip>
                        <Button
                            className="close-button"
                            onClick={handleClose}>
                            Close
                        </Button>
                    </div>
                </>
            )}

            {selectedEntry?.siteEncryptedId && (
                <DoorCctvCodesModal
                    visible={doorCodesVisible}
                    onClose={() => setDoorCodesVisible(false)}
                    encryptedId={selectedEntry.siteEncryptedId} />
            )}
        </Modal>
    );
};

export default FtcUpdateModal;
